import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import { posterSrc, formatDateOnly, formatCurrency } from "../lib/api";
import { ArrowLeft, ArrowRight, MapPin, Calendar, Sparkles } from "lucide-react";

export default function SpotlightCarousel({ events }) {
  const [emblaRef, embla] = useEmblaCarousel({ loop: events.length > 1, align: "start" });
  const [idx, setIdx] = useState(0);

  const onSelect = useCallback(() => { if (embla) setIdx(embla.selectedScrollSnap()); }, [embla]);
  useEffect(() => {
    if (!embla) return;
    embla.on("select", onSelect);
    const t = setInterval(() => embla.scrollNext(), 5000);
    return () => { clearInterval(t); embla.off("select", onSelect); };
  }, [embla, onSelect]);

  if (!events.length) {
    return (
      <div data-testid="spotlight-empty" className="relative flex aspect-[4/5] flex-col items-center justify-center rounded-3xl border border-dashed border-zinc-700 bg-[#3A1020] p-8 text-center">
        <Sparkles className="mb-3 h-8 w-8 text-punch-500" />
        <div className="font-display text-2xl font-extrabold text-zinc-50">This spot is for sale</div>
        <p className="mt-2 text-sm text-zinc-400">Organizers: put your event in the spotlight for ₹1,999 / week.</p>
        <Link to="/register?role=organizer" className="mt-4 rounded-full bg-punch-500 px-5 py-2 text-xs font-bold uppercase tracking-widest text-white hover:bg-punch-600">Promote an event</Link>
      </div>
    );
  }

  return (
    <div data-testid="spotlight-carousel" className="relative">
      <div className="absolute -left-3 -top-3 z-20 sticker rounded-full bg-punch-500 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-white glow-punch transition">Spotlight · Sponsored</div>
      <div ref={emblaRef} className="overflow-hidden rounded-3xl border border-amber-500/20 glow-amber">
        <div className="flex">
          {events.map((e) => {
            const min = e.tiers?.length ? Math.min(...e.tiers.map((t) => t.price)) : 0;
            return (
              <Link key={e.id} to={`/events/${e.id}`} data-testid={`spotlight-slide-${e.id}`} className="relative min-w-0 flex-[0_0_100%]">
                <div className="relative aspect-[4/5]">
                  <img src={posterSrc(e.poster_url)} alt={e.title} className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#2B0B15] via-[#2B0B15]/30 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-6">
                    <div className="mb-2 flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-amber-400">
                      <span className="rounded-full border border-amber-500/40 px-2 py-0.5">{e.category}</span>
                      <span className="text-zinc-400">by {e.organizer_name}</span>
                    </div>
                    <div className="font-display text-2xl font-extrabold leading-tight text-zinc-50 sm:text-3xl">{e.title}</div>
                    <div className="mt-2 flex flex-wrap gap-3 text-xs text-zinc-300">
                      <span className="flex items-center gap-1"><Calendar className="h-3 w-3 text-amber-500" />{formatDateOnly(e.date)}</span>
                      <span className="flex items-center gap-1"><MapPin className="h-3 w-3 text-amber-500" />{e.venue}, {e.city}</span>
                      <span className="font-mono font-bold text-amber-400">from {formatCurrency(min)}</span>
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
      {events.length > 1 && (
        <div className="mt-3 flex items-center justify-between">
          <div className="flex gap-1.5">
            {events.map((_, i) => (
              <button key={i} onClick={() => embla?.scrollTo(i)} data-testid={`spotlight-dot-${i}`} className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-amber-500" : "w-1.5 bg-zinc-700"}`} />
            ))}
          </div>
          <div className="flex gap-2">
            <button data-testid="spotlight-prev" onClick={() => embla?.scrollPrev()} className="rounded-full border border-zinc-700 p-2 text-zinc-300 transition hover:border-amber-500 hover:text-amber-400"><ArrowLeft className="h-4 w-4" /></button>
            <button data-testid="spotlight-next" onClick={() => embla?.scrollNext()} className="rounded-full border border-zinc-700 p-2 text-zinc-300 transition hover:border-amber-500 hover:text-amber-400"><ArrowRight className="h-4 w-4" /></button>
          </div>
        </div>
      )}
    </div>
  );
}
